import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchUsers } from '@api/backoffice';
import type { UserSortKey } from '@api/backoffice';
import ThSortable from '@components/Backoffice/ThSortable';
import ExportButton from '@components/Backoffice/ExportButton';
import { downloadBackofficeCsv } from '@utils/csvExport';
import {
  PageTitle,
  Card,
  CardHeader,
  Table,
  SearchBar,
  Badge,
  SmallText,
  PaginationBar,
  PaginationInfo,
  PaginationButton,
  PaginationActions,
  EmptyState,
} from '@components/Backoffice/BackofficeLayout.styled';
import type { UserSummary, PageData } from './types';
import { useBackofficeSort } from './useBackofficeSort';

const BackofficeUsers: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<PageData<UserSummary> | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const { sortKey, sortDirection, toggleSort } = useBackofficeSort<UserSortKey>('createdAt');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchUsers({
        page,
        size: 20,
        search: search || undefined,
        sort: sortKey,
        direction: sortDirection,
      });
      setData(result);
    } finally {
      setLoading(false);
    }
  }, [page, search, sortKey, sortDirection]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSort = (key: string) => {
    toggleSort(key as UserSortKey);
    setPage(0);
  };

  const roleVariant = (role: string) => {
    if (role === 'AIBUILD_ADMIN') return 'info' as const;
    if (role === 'ORG_ADMIN') return 'success' as const;
    return 'warning' as const;
  };

  const th = (label: string, key: string) => (
    <ThSortable
      label={label}
      sortKey={key}
      activeKey={sortKey}
      direction={sortDirection}
      onToggle={handleSort}
    />
  );

  return (
    <>
      <PageTitle>Users</PageTitle>
      <Card>
        <CardHeader>
          <SearchBar
            placeholder="Search by email or name..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />
          <ExportButton
            onExport={() =>
              downloadBackofficeCsv(
                '/api/v1/backoffice/users/export',
                { search: search || '', sort: sortKey, direction: sortDirection },
                `users_${new Date().toISOString().slice(0, 10)}.csv`
              )
            }
          />
        </CardHeader>
        {loading && !data && <EmptyState>Loading...</EmptyState>}
        {!loading && data?.content.length === 0 && (
          <EmptyState>No users found</EmptyState>
        )}
        {data && data.content.length > 0 && (
          <>
            <Table>
              <thead>
                <tr>
                  {th('Email', 'email')}
                  {th('Name', 'displayName')}
                  {th('Role', 'orgRole')}
                  {th('Organization', 'organizationName')}
                  {th('Expires', 'expiryDate')}
                  {th('Last Login', 'lastLoginAt')}
                </tr>
              </thead>
              <tbody>
                {(data?.content ?? []).map((user) => (
                  <tr
                    key={user.id}
                    onClick={() => navigate(`/backoffice-admin/users/${user.id}`)}
                  >
                    <td>
                      {user.email}
                      {user.bannedAt && (
                        <> <Badge $variant="warning">BANNED</Badge></>
                      )}
                    </td>
                    <td>{user.displayName || '—'}</td>
                    <td>
                      <Badge $variant={roleVariant(user.orgRole)}>{user.orgRole}</Badge>
                    </td>
                    <td>
                      {user.organizationName || '—'}
                      {user.organizationId && (
                        <>
                          <br />
                          <SmallText>{user.organizationId}</SmallText>
                        </>
                      )}
                    </td>
                    <td>
                      {user.expiryDate
                        ? new Date(user.expiryDate).toLocaleString()
                        : '—'}
                    </td>
                    <td>
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleString()
                        : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {data && data.totalPages > 1 && (
              <PaginationBar>
                <PaginationInfo>
                  Page {data.number + 1} of {data.totalPages} ({data.totalElements} total)
                </PaginationInfo>
                <PaginationActions>
                  <PaginationButton
                    $disabled={page === 0}
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    Previous
                  </PaginationButton>
                  <PaginationButton
                    $disabled={page >= data.totalPages - 1}
                    disabled={page >= data.totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </PaginationButton>
                </PaginationActions>
              </PaginationBar>
            )}
          </>
        )}
      </Card>
    </>
  );
};

export default BackofficeUsers;
